
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { IComponentDecorator, IDecoratedProps, withDisplayName } from './Decorators';
import { IRenderProps } from './RenderProps';
import { Facet, IFaceted } from '../../models/Facet';
import { Fragment } from './Fragment';

/**
 * @public
 */
export interface IContextComponents<TContext> {
  Provider: React.ComponentType<{ value: TContext; }>;
  Consumer: React.ComponentType<IRenderProps<TContext>>;
}

/**
 * @public
 */
export function createContext<TContext>(name: string, defaultValue?: TContext): IContextComponents<TContext> {
  const facet = new Facet<TContext>(name);

  const contextTypes: React.ValidationMap<IFaceted<TContext>> = {
    [facet.id]: defaultValue ? PropTypes.any : PropTypes.any.isRequired
  };

  const Consumer = withDisplayName(`Context.Consumer(${name})`)(connectToContext(
    contextTypes,
    (context: IFaceted<TContext>): { value: TContext; } => {
      return {
        value: facet.unpack(context)!
      };
    })((props: IRenderProps<TContext> & React.Props<{}> & { value: TContext; }) => {
      const {
        value = defaultValue,
        children
      } = props;

      return typeof children === 'function' ?
        children(value!) :
        <Fragment>{ children }</Fragment>;
    }));

  const Provider = withDisplayName(`Context.Provider(${name})`)(provideContext(
    contextTypes,
    (props: { value: TContext; }): IFaceted<TContext> => {
      return facet.pack(props.value);
    }));

  return {
    Provider,
    Consumer
  };
}

/**
 * @internal
 */
export function connectToContext<TContext, TInjectedProps, TOwnProps>(
  contextTypes: PropTypes.ValidationMap<TContext>,
  mapContextToProps: (context: TContext, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): React.StatelessComponent<IDecoratedProps<P, TInjectedProps, TOwnProps>> => {
    const Component = component;

    const Connector: React.StatelessComponent<IDecoratedProps<P, TInjectedProps, TOwnProps>> =
      withDisplayName('ConnectToContext')((props: IDecoratedProps<P, TInjectedProps, TOwnProps>, context: TContext) => {
        return (
          <Component
            { ...props }
            { ...mapContextToProps(context, props as TOwnProps) }
          />
        );
      });

    Connector.contextTypes = contextTypes;

    return Connector;
  };
}

/**
 * @internal
 */
export function provideContext<TContext, TProps>(
  contextTypes: PropTypes.ValidationMap<TContext>,
  mapPropsToContext: (props: TProps) => TContext
): React.ComponentType<TProps> {
  class Provider extends React.PureComponent<TProps> {
    public static readonly childContextTypes: PropTypes.ValidationMap<TContext> = contextTypes;

    public getChildContext(): TContext {
      return mapPropsToContext(this.props);
    }

    public render(): JSX.Element | null {
      return (
        <Fragment>{ this.props.children }</Fragment>
      );
    }
  }

  return withDisplayName('ProvideContext')(Provider);
}
